import {
  Checkbox,
  Input,
  InputIconWrapper,
  NumberInput,
  TextArea
} from '@library/components/inputs';
import { useEffect, useMemo, useState } from 'react';
import { CREATE_MISSION_FIXED_CACHE_KEY } from '@bo-constants/missions';
import { FormActionButtons } from '@library/components/buttons';
import { PayoutTypeSelect } from '@library/components/selects/PayoutTypeSelect';
import { PrizepicksDatePicker } from '@library/components/datePicker';
import type { PromotionPayoutType } from '@library/types/promotions';
import { PromotionsSelect } from '@library/components/selects/PromotionsSelect';
import { useCreateMissionMutation } from '@bo-services/promotionsApi';
import { useNavigate } from 'react-router-dom';
import { useSteps } from '@library/components/progress/Steps';

export const MissionDetailsForm = () => {
  const [createMission, { isSuccess }] = useCreateMissionMutation({
    fixedCacheKey: CREATE_MISSION_FIXED_CACHE_KEY
  });
  const navigate = useNavigate();
  const { setCurrentStep, setStepComplete } = useSteps();

  const [missionName, setMissionName] = useState('');
  const [missionDescription, setMissionDescription] = useState('');
  const [missionLongDescription, setMissionLongDescription] = useState('');
  const [eligibleForAll, setEligibleForAll] = useState(false);
  const [optIn, setOptIn] = useState(false);
  const [optInBy, setOptInBy] = useState<Date>();
  const [completeBy, setCompleteBy] = useState<Date>();
  const [payoutType, setPayoutType] = useState<PromotionPayoutType>('promo');
  const [payoutAmountUsd, setPayoutAmountUsd] = useState<number>(0);
  const [promotionId, setPromotionId] = useState<number>(0);
  const [completeByDatePickerOpen, setCompleteByDatePickerOpen] =
    useState(false);
  const [optInByDatePickerOpen, setOptInByDatePickerOpen] = useState(false);

  const isOptInByInvalid = useMemo(() => {
    if (optInBy) {
      return optInBy < new Date(Date.now());
    }

    return false;
  }, [optInBy]);
  const isCompleteByInvalid = useMemo(() => {
    if (completeBy) {
      if (optInBy) {
        return completeBy < optInBy;
      }

      return completeBy < new Date(Date.now());
    }

    return false;
  }, [completeBy, optInBy]);

  const isFormInvalid = useMemo(
    () =>
      isOptInByInvalid ||
      isCompleteByInvalid ||
      !missionName ||
      missionName === '' ||
      missionDescription === '' ||
      !missionDescription ||
      payoutAmountUsd < 1,
    [
      missionDescription,
      missionName,
      isCompleteByInvalid,
      isOptInByInvalid,
      payoutAmountUsd
    ]
  );

  const resetFields = () => {
    setMissionName('');
    setMissionDescription('');
    setOptIn(false);
    setOptInBy(undefined);
    setCompleteBy(undefined);
    setEligibleForAll(false);
    setPayoutType('promo');
    setPayoutAmountUsd(0);
  };

  const handleOptInByChange = (date: Date) => {
    setOptInBy(date);
    setOptIn(true);
  };

  const handleCancel = () => {
    resetFields();
    navigate('/missions');
  };

  const handleSaveAndContinue = async () => {
    // TODO: if isComplete() && isFormDirty() then update

    if (isFormInvalid) {
      return;
    }

    const mission = {
      name: missionName,
      description: missionDescription,
      eligible_for_all: eligibleForAll,
      complete_by: String(completeBy),
      long_description: missionDescription,
      opt_in: optIn,
      opt_in_by: String(optInBy),
      payout_amount_usd: payoutAmountUsd,
      payout_type: payoutType,
      promotion_id: 0
    };

    await createMission({
      mission
    });
  };

  useEffect(() => {
    if (isSuccess) {
      setStepComplete();
      setCurrentStep('events');
    }
  }, [isSuccess, setStepComplete, setCurrentStep]);

  return (
    <>
      <div className="flex gap-x-4">
        <div className="flex w-1/2 flex-col gap-y-2">
          <Input
            className="bg-soFresh-110 text-soClean-100 border-soFresh-140 placeholder:text-soClean-170 block min-h-10 w-full rounded-md border py-1.5 sm:text-sm sm:leading-6"
            labelName="Name"
            value={missionName}
            onValueChange={setMissionName}
            required
          />
        </div>
        <div className="flex w-1/2 flex-col gap-y-2">
          <Input
            className="bg-soFresh-110 text-soClean-100 border-soFresh-140 placeholder:text-soClean-170 block min-h-10 w-full rounded-md border py-1.5 sm:text-sm sm:leading-6"
            labelName="Description"
            value={missionDescription}
            onValueChange={setMissionDescription}
            required
          />
        </div>
      </div>

      <div className="flex flex-col gap-y-2">
        <TextArea
          labelName="Long Description"
          value={missionLongDescription}
          onValueChange={setMissionLongDescription}
        />
      </div>

      <div className="flex w-full items-center gap-4">
        <div className="flex w-1/2 flex-col gap-y-2">
          <p className="text-soClean-100 rounded-md text-xs uppercase leading-6">
            Opt-in by
          </p>
          <PrizepicksDatePicker
            handleChange={handleOptInByChange}
            open={optInByDatePickerOpen}
            setOpen={setOptInByDatePickerOpen}
            monthsShown={1}
            invalid={isOptInByInvalid}
            value={optInBy}
          />
          {isOptInByInvalid ? (
            <p className="text-roses text-xs">
              {optInBy && isOptInByInvalid && 'Are you a time traveler?'}
            </p>
          ) : (
            <p className="text-xs">&nbsp;</p>
          )}
        </div>
        <div className="flex w-1/2 flex-col gap-y-2">
          <p className="text-soClean-100 rounded-md text-xs uppercase leading-6">
            Complete by
          </p>
          <PrizepicksDatePicker
            handleChange={(date) => setCompleteBy(date)}
            open={completeByDatePickerOpen}
            setOpen={setCompleteByDatePickerOpen}
            monthsShown={1}
            invalid={isCompleteByInvalid}
            value={completeBy}
          />
          {isCompleteByInvalid ? (
            <p className="text-roses text-xs">
              Complete by date must be after Opt in by date
            </p>
          ) : (
            <p className="text-xs">&nbsp;</p>
          )}
        </div>
      </div>

      <div className="flex w-full items-start gap-4">
        <div className="flex w-1/3 flex-col gap-y-2">
          <PayoutTypeSelect
            containerClassName="max-w-[100%]"
            className="w-full max-w-full"
            onChange={(value) => setPayoutType(value)}
            label="Payout Type"
            value={payoutType}
          />
        </div>
        <div className="flex w-1/3 flex-col">
          <InputIconWrapper
            className="flex flex-col gap-y-2"
            icon="$"
            iconClassName="mt-1"
          >
            <NumberInput
              min={1}
              className="bg-soFresh-110 text-soClean-100 border-soFresh-140 placeholder:text-soClean-170 block min-h-10 w-full rounded-md border py-1.5 pl-10 sm:text-sm sm:leading-6"
              labelClassName="text-xs rounded-md uppercase leading-6 text-soClean-100"
              labelName="Payout Amount"
              onValueChange={(value) => setPayoutAmountUsd(Number(value))}
            />
          </InputIconWrapper>
        </div>

        <div className="flex w-1/3 flex-col gap-y-2">
          <PromotionsSelect
            containerClassName="max-w-[100%]"
            className="w-full max-w-full"
            onChange={(value) => setPromotionId(Number(value))}
            label="Payout Type"
            value={String(promotionId)}
          />
        </div>

        <div className="mt-7 flex flex-col gap-y-2">
          <div className="flex items-center">
            <Checkbox
              id="optIn"
              label="Opt-in"
              labelClassName="text-xs uppercase"
              disabled={!!optInBy}
              checked={optIn}
              onCheckboxChange={({ checked }) => setOptIn(checked)}
            />
          </div>
          <div className="flex items-center">
            <Checkbox
              id="forAll"
              label="Eligible for all"
              labelClassName="text-xs uppercase"
              checked={eligibleForAll}
              onCheckboxChange={({ checked }) => setEligibleForAll(checked)}
            />
          </div>
        </div>
      </div>

      <FormActionButtons
        primary={{
          onClick: handleSaveAndContinue,
          label: 'Continue',
          disabled: isFormInvalid
        }}
        secondary={{ onClick: handleCancel, label: 'Cancel' }}
      />
    </>
  );
};
